import { format } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { ANALYTICS_CART_DEALER_ID, PRODUCT_HAS_CONFIGURATOR, VARIANTS_PRICES_DISCOUNT, } from '@yme/common/constants';
import { DATE_FORMAT } from '@yme/molecules/date-picker/constants';
import { PARAMS_KEYS } from '@yme/organisms/news-events-grid/constants';
import { BLOOMREACH_QUERY_ANALYTIC_SORT_MAPPING } from '@yme/utils/bloomreach/constants';
import { getInRiverId, getMasterVariantSku, getTestRideUrl } from '@yme/utils/product';
import { GUEST_USER_NAME, DigitalDataMutation, STATE_FOR_PRODUCT_OPTIONS_CLICK, GENERIC_CLICK_STATE, ListingMap, DeduplicatedData, DEFAULT_PRODUCT_QUANTITY, } from './constants';
import { configuratorParamsToEventInfo, cursorToPagination, emptyDealerLocator, extractSlug, normalizeDate, normalizeLocation, normalizeString, parseFilters, parseSort, attrValue, isPDPPage, captureEventToState, closestDataTestIdFromTarget, getDigitalData, mapToAdobeDataLayerProduct, serializeCart as serializeCartFunc, entryToLineItem, serializeProductData, categoryNameFromKey, adobeDataLayer, mapToAdobeDataLayerBloomreachProduct, parseBloomreachSort, parseBloomreachFilters, parseBloomreachQueryParams, } from './digital-data-utils';
document.addEventListener('keydown', (e) => {
    if (e.code !== 'Enter')
        return;
    captureEventToState(GENERIC_CLICK_STATE, 10);
}, {
    capture: true,
});
document.addEventListener('click', (e) => {
    if (!e.target)
        return;
    captureEventToState(GENERIC_CLICK_STATE, 100);
    const testId = closestDataTestIdFromTarget(e.target);
    if (!testId)
        return;
    captureEventToState(STATE_FOR_PRODUCT_OPTIONS_CLICK);
}, {
    capture: true,
});
class AdobeLaunchLoader {
    isLoading = false;
    loadPromise;
    // eslint-disable-next-line class-methods-use-this
    resolve() {
        // EOL
    }
    // eslint-disable-next-line class-methods-use-this
    reject(msg) {
        throw new Error(msg);
    }
    constructor() {
        this.loadPromise = new Promise((res, rej) => {
            this.resolve = res;
            this.reject = rej;
        });
    }
    // Returns the appropriate script URL based on the environment
    getScriptURL() {
        const selector = `meta[data-adobe-data-layer-url]`;
        const node = document.head.querySelector(selector);
        if (!node) {
            return null;
        }
        const url = node.dataset.adobeDataLayerUrl;
        if (url && url.length) {
            return url;
        }
        else {
            return null;
        }
    }
    // Loads the Adobe Launch script asynchronously
    async loadScript() {
        if (this.isLoading) {
            return this.loadPromise;
        }
        this.isLoading = true;
        const script = document.createElement('script');
        const url = this.getScriptURL();
        if (!url) {
            this.isLoading = false;
            this.reject();
            return this.loadPromise;
        }
        script.src = url;
        script.defer = true;
        script.onload = () => {
            this.isLoading = false;
            this.resolve();
        };
        script.onerror = () => {
            this.isLoading = false;
            this.reject('Failed to load the Adobe Launch script');
        };
        document.head.appendChild(script);
        return this.loadPromise;
    }
    // eslint-ignore-next-line class-methods-use-this
    unref(payload) {
        return JSON.parse(JSON.stringify(payload));
    }
    push(rawData) {
        const data = this.unref(rawData);
        this.loadPromise.then(() => {
            // Data layer enablement check, required on author/webpack environment
            adobeDataLayer()?.push(data);
        });
    }
}
export const adobeLaunch = new AdobeLaunchLoader();
export const digitalData = adobeLaunch;
export function setDigitalData(type, payload, ddCurrencyRate) {
    const dd = getDigitalData();
    const rate = Number(ddCurrencyRate?.rate);
    const serializeCart = (input, getPdpLinkByCategory, isLineItemsEmpty = false) => {
        return serializeCartFunc(input, getPdpLinkByCategory, rate, isLineItemsEmpty);
    };
    const breadcrumbs = () => dd?.data?.page?.breadcrumb?.split(',').filter((name) => {
        return name !== 'pdp' && name !== 'book' && name !== 'test-ride';
    }) ?? [];
    if (type === DigitalDataMutation.ADD_TO_WISHLIST) {
        const { sku, pagePath } = payload;
        dd.eventInfo = {
            skuCode: normalizeString(sku),
            id: normalizeString(extractSlug(pagePath)),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.REMOVE_FROM_WISHLIST) {
        const { sku, pagePath } = payload;
        dd.eventInfo = {
            skuCode: normalizeString(sku),
            id: normalizeString(extractSlug(pagePath)),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_AVAILABILITY) {
        const { available } = payload;
        dd.eventInfo = {
            available,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PLP_VIEW_FILTER_RESULT) {
        const { searchTerm, facets, query, sort, resolvedFacets } = payload;
        const hasDiscountFlag = query.includes(VARIANTS_PRICES_DISCOUNT);
        const filters = parseFilters(facets);
        const sortBy = parseSort(sort);
        const facetsMap = resolvedFacets.reduce((acc, facet) => {
            acc[facet.id.split('.').pop()] = facet.values;
            return acc;
        }, {});
        filters.forEach((filter) => {
            const [key, opts] = filter;
            filter[1] = opts.map((opt) => {
                const facet = facetsMap[key].find((f) => f.value === opt);
                if (facet) {
                    return facet.valueEn || facet.value;
                }
                return opt;
            });
        });
        filters.unshift(['isDiscounted', [String(hasDiscountFlag)]]);
        const urlParams = new URLSearchParams(window.location.search);
        const urlParamsKeys = Array.from(urlParams.keys());
        const queryParams = urlParamsKeys.reduce((acc, key) => {
            const qpKey = key.split('.').pop();
            if (qpKey) {
                acc[qpKey] = urlParams.getAll(key);
            }
            return acc;
        }, {});
        Object.keys(queryParams).forEach((key) => {
            if (key in facetsMap) {
                const values = queryParams[key][0].split(',');
                const facetValues = facetsMap[key].filter((facet) => values.includes(facet.value ?? '') || values.includes(facet.valueEn ?? ''));
                queryParams[key][0] = facetValues.map((facet) => facet.valueEn || facet.value).join(',');
            }
        });
        dd.eventInfo = {
            searchTerm: normalizeString(searchTerm.trim(), ''),
            queryParams,
            filterBy: filters.map(([key, opts]) => {
                return {
                    filterField: key,
                    filterValues: opts.map((opt) => opt),
                };
            }),
            sortBy: sortBy.map(([key, directions]) => {
                return {
                    sortField: key,
                    sortDirection: normalizeString(directions[0]),
                };
            }),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PLP_VIEW_FILTER_RESULT_BLOOMREACH) {
        const { searchTerm, facets, sort, accessoryTypeTranslations, accessoryUsageTranslations, genderTranslations, subcategoryTranslations, } = payload;
        const sortBy = parseBloomreachSort(sort);
        const filterBy = parseBloomreachFilters(facets, genderTranslations, accessoryTypeTranslations, accessoryUsageTranslations, subcategoryTranslations);
        const urlParams = new URLSearchParams(window.location.search);
        const urlParamsArray = Array.from(urlParams);
        const queryParams = parseBloomreachQueryParams(urlParamsArray, genderTranslations, accessoryTypeTranslations, accessoryUsageTranslations, subcategoryTranslations);
        dd.eventInfo = {
            searchTerm: normalizeString(searchTerm.trim(), ''),
            queryParams,
            filterBy,
            sortBy,
        };
        dd.event = DigitalDataMutation.PLP_VIEW_FILTER_RESULT;
    }
    else if (type === DigitalDataMutation.SEGMENT_PANEL_CLICK) {
        const { segmentName, url } = payload;
        if (!url.includes('test-ride')) {
            return;
        }
        dd.eventInfo = {
            category: normalizeString(segmentName),
        };
        dd.event = DigitalDataMutation.TEST_RIDE_SELECT_CATEGORY;
    }
    else if (type === DigitalDataMutation.SMART_FIT_USAGE_INSIGHTS) {
        const { event, payload: eventPayload } = payload;
        dd.eventInfo = {
            ...eventPayload,
        };
        dd.event = `${DigitalDataMutation.SMART_FIT_USAGE_INSIGHTS}:${event}`;
    }
    else if (type === DigitalDataMutation.SMART_FIT_START) {
        const { sku } = payload;
        dd.eventInfo = {
            skuCode: normalizeString(sku),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_SELECT_ITEM) {
        const { sku } = payload;
        let eventType = DigitalDataMutation.TEST_RIDE_SELECT_ITEM;
        if (dd.data.page.category === 'comparison-page-template') {
            eventType = DigitalDataMutation.COMPARE_TEST_RIDE_SELECT_ITEM;
        }
        dd.eventInfo = {
            skuCode: normalizeString(sku),
        };
        dd.event = eventType;
    }
    else if (type === DigitalDataMutation.CART_SHARE_QR ||
        type === DigitalDataMutation.CART_SHARE_EMAIL) {
        const { dealerId } = payload;
        dd.eventInfo = {
            dealerId: normalizeString(dealerId),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.ACC_SELECT_MODEL_FILTER) {
        const { searchType, product } = payload;
        const publishedCategory = product.categories.find((category) => category.published);
        const categoryName = categoryNameFromKey(publishedCategory?.key ?? '');
        const masterVariant = product.variants.find((variant) => variant.master);
        dd.eventInfo = {
            searchType,
            product: {
                id: normalizeString(product.slug),
                skuCode: normalizeString(masterVariant?.sku ?? ''),
                name: product.productNameEn ?? product.name,
                category: categoryName,
                quantity: DEFAULT_PRODUCT_QUANTITY,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.MAINTENANCE_CONFIRMATION ||
        type === DigitalDataMutation.LEAD_CONFIRMATION) {
        const { preferredDate, dealerName, inputLocation, preferredTime, followUp, selectedDealerId, marketingChannel, } = payload;
        dd.eventInfo = {
            date: preferredDate ? normalizeString(normalizeDate(preferredDate)) : '',
            hour: normalizeString(preferredTime),
            method: normalizeString(followUp),
            location: normalizeLocation(inputLocation),
            dealerId: selectedDealerId,
            dealerName,
            marketingChannel: normalizeString(marketingChannel),
        };
        if (type === DigitalDataMutation.LEAD_CONFIRMATION) {
            delete dd.eventInfo.hour;
            delete dd.eventInfo.date;
            delete dd.eventInfo.method;
        }
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CONFIGURATOR_CONFIRMATION) {
        const { configuratorUrl, configuratorData, marketingChannel } = payload;
        dd.eventInfo = {
            ...configuratorParamsToEventInfo(configuratorData, configuratorUrl),
            marketingChannel: normalizeString(marketingChannel),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.DEALER_LOCATOR_PLAN_A_ROUTE ||
        type === DigitalDataMutation.DEALER_LOCATOR_CONTACT) {
        const { id, name, contact } = payload;
        dd.eventInfo = {
            dealer: {
                id,
                name,
                contact,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.DEALER_LOCATOR_SELECT_SERVICE) {
        const { services } = payload;
        if (!services.length) {
            return;
        }
        dd.data.dealerLocator.services = services.map((s) => normalizeString(s));
        dd.eventInfo = {
            dealer: {
                services: services.map((s) => normalizeString(s)),
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.LOGOUT) {
        dd.data.user.profile.profileInfo.userName = GUEST_USER_NAME;
        return;
    }
    else if (type === DigitalDataMutation.COMPARE_SPECIFICATIONS_CLICK) {
        const rawSection = payload;
        dd.eventInfo = {
            section: normalizeString(rawSection),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.COMPATIBILITY_SEARCH_PRODUCT) {
        if (!isPDPPage()) {
            return;
        }
        const { term } = payload;
        dd.eventInfo = {
            searchTerm: term,
            productId: normalizeString(dd.data.product.id),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CONFIGURATOR_SELECT_DEALER) {
        const { location, dealerId, dealerName, configuratorData, configuratorUrl } = payload;
        dd.eventInfo = {
            location: normalizeLocation(location),
            dealerId: normalizeString(dealerId),
            dealerName,
            ...configuratorParamsToEventInfo(configuratorData, configuratorUrl),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CONFIGURATOR_SELECT_DEALER) {
        const { location, dealerId, dealerName } = payload;
        dd.eventInfo = {
            location: normalizeLocation(location),
            dealerId: normalizeString(dealerId),
            dealerName,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_SELECT_DEALER) {
        const { location, dealerId, dealerName, sku, productSlug } = payload;
        dd.eventInfo = {
            location: normalizeLocation(location),
            dealerId: normalizeString(dealerId),
            dealerName,
            skuCode: normalizeString(sku),
            category: normalizeString(breadcrumbs().pop() ?? ''),
            productId: normalizeString(productSlug),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.DEALER_LOCATOR_SELECT_DEALER) {
        const { id, name } = payload;
        dd.data.dealerLocator.name = normalizeString(name);
        dd.data.dealerLocator.id = normalizeString(id);
        dd.eventInfo = {
            dealer: {
                id,
                name,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.DEALER_LOCATOR_SELECT_CATEGORY) {
        const rawCategory = payload;
        const category = normalizeString(rawCategory);
        dd.data.dealerLocator.category = category;
        dd.eventInfo = {
            dealer: {
                category,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.DEALER_LOCATOR_SELECT_LOCATION) {
        const location = payload;
        dd.data.dealerLocator.location = normalizeLocation(location);
        dd.eventInfo = {
            dealer: {
                location: normalizeLocation(location),
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.DEALER_LOCATOR_SELECT_COUNTRY) {
        const rawCountry = payload;
        const country = normalizeString(rawCountry);
        dd.data.dealerLocator = emptyDealerLocator();
        dd.data.dealerLocator.country = country;
        dd.eventInfo = {
            dealer: {
                country,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.COMPATIBILITY_FORM_ENTRY ||
        type === DigitalDataMutation.COMPATIBILITY_CLICK) {
        if (!isPDPPage()) {
            return;
        }
        dd.eventInfo = {
            productId: normalizeString(dd.data.product.id),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.DEALER_LOCATOR_PAGE_VIEW) {
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CONFIGURATOR_START) {
        if (!isPDPPage()) {
            return;
        }
        dd.eventInfo = {
            category: normalizeString(breadcrumbs().pop() ?? ''),
            productId: normalizeString(dd.data.product.id),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.COMPARE_VIEW) {
        const { products, getPdpLinkByCategory } = payload;
        const listItems = products.map((p) => ({
            product: mapToAdobeDataLayerProduct(p, getPdpLinkByCategory(p.categories)),
        }));
        const listId = ListingMap[type];
        // remove old listing with same id
        dd.data.listings = dd.data.listings.filter((l) => l.listing.id !== listId);
        if (listItems.length) {
            dd.data.listings.push({
                listing: {
                    name: listId,
                    id: listId,
                    listItems,
                },
            });
        }
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_CONFIRMATION) {
        const { preferredDate, dealerName, inputLocation, preferredTime, followUp, productId, selectedDealerId, productSlug, marketingChannel, productType, } = payload;
        dd.eventInfo = {
            date: normalizeString(normalizeDate(preferredDate)),
            hour: normalizeString(preferredTime),
            method: normalizeString(followUp),
            category: normalizeString(breadcrumbs().pop() ?? ''),
            skuCode: normalizeString(productId),
            productId: normalizeString(productSlug),
            location: normalizeLocation(inputLocation),
            marketingChannel: normalizeString(marketingChannel),
            productType: normalizeString(productType),
            dealerId: selectedDealerId,
            dealerName,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.COMPARE_SELECT_ITEM) {
        const { sku } = payload;
        dd.eventInfo = {
            skuCode: normalizeString(sku),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_CHANGE_MODEL) {
        const { sku } = payload;
        dd.eventInfo = {
            skuCode: normalizeString(sku),
            category: normalizeString(breadcrumbs().pop() ?? ''),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_SEARCH_DEALER) {
        const { sku, location, productSlug } = payload;
        dd.eventInfo = {
            category: normalizeString(breadcrumbs().pop() ?? ''),
            skuCode: normalizeString(sku),
            location: normalizeLocation(location),
            productId: normalizeString(productSlug),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CONFIGURATOR_SEARCH_DEALER) {
        const { location, configuratorData, configuratorUrl } = payload;
        const castedType = type;
        if (DeduplicatedData[castedType].has(location)) {
            return;
        }
        DeduplicatedData[castedType].add(location);
        dd.eventInfo = {
            location: normalizeLocation(location),
            ...configuratorParamsToEventInfo(configuratorData, configuratorUrl),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.MAINTENANCE_SEARCH_DEALER ||
        type === DigitalDataMutation.LEAD_SEARCH_DEALER) {
        const { location } = payload;
        const castedType = type;
        if (DeduplicatedData[castedType].has(location)) {
            return;
        }
        DeduplicatedData[castedType].add(location);
        dd.eventInfo = {
            location: normalizeLocation(location),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_TECH_SPECS) {
        dd.eventInfo = {};
        dd.event = type;
    }
    if (type === DigitalDataMutation.CONFIGURATOR_FORM_ENTRY) {
        const { configuratorData, configuratorUrl } = payload;
        dd.eventInfo = configuratorParamsToEventInfo(configuratorData, configuratorUrl);
        dd.event = type;
    }
    else if (type === DigitalDataMutation.MAINTENANCE_FORM_ENTRY ||
        type === DigitalDataMutation.LEAD_FORM_ENTRY) {
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CHANGE_SHIPPING_METHOD ||
        type === DigitalDataMutation.CHECKOUT_CHANGE_SHIPPING_METHOD) {
        const { method, cart, getPdpLinkByCategory } = payload;
        dd.data.cart = serializeCart(cart, getPdpLinkByCategory);
        dd.eventInfo = {
            shippingMethod: normalizeString(method),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.COMPATIBILITY_RESULT_POSITIVE ||
        type === DigitalDataMutation.COMPATIBILITY_RESULT_NEGATIVE) {
        if (!isPDPPage()) {
            return;
        }
        const { productId } = payload;
        dd.eventInfo = {
            compatibleProductId: normalizeString(productId),
            productId: normalizeString(dd.data.product.id),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.COMPATIBILITY_SELECT_PRODUCT) {
        if (!isPDPPage()) {
            return;
        }
        const { productId, term } = payload;
        dd.eventInfo = {
            compatibleProductId: normalizeString(productId),
            productId: normalizeString(dd.data.product.id),
            searchTerm: term,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_SELECT_CONTACT_METHOD) {
        const { followUp, productSlug, sku } = payload;
        dd.eventInfo = {
            method: normalizeString(followUp),
            productId: normalizeString(productSlug),
            skuCode: normalizeString(sku),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.MAINTENANCE_SELECT_CONTACT_METHOD) {
        const { followUp } = payload;
        dd.eventInfo = {
            method: normalizeString(followUp),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_SELECT_DATE) {
        const { preferredDate, productSlug, sku } = payload;
        // date shoulod look like 20231231 (YYYYMMDD)
        dd.eventInfo = {
            date: normalizeString(normalizeDate(preferredDate)),
            productId: normalizeString(productSlug),
            skuCode: normalizeString(sku),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.MAINTENANCE_SELECT_DATE) {
        const { preferredDate } = payload;
        // date shoulod look like 20231231 (YYYYMMDD)
        dd.eventInfo = {
            date: normalizeString(normalizeDate(preferredDate)),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_SELECT_HOUR) {
        const { preferredTime, productSlug, sku } = payload;
        dd.eventInfo = {
            hour: normalizeString(preferredTime),
            productId: normalizeString(productSlug),
            skuCode: normalizeString(sku),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.MAINTENANCE_SELECT_HOUR) {
        const { preferredTime } = payload;
        dd.eventInfo = {
            hour: normalizeString(preferredTime),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_FORM_ENTRY) {
        const { sku, productSlug, productType } = payload;
        dd.eventInfo = {
            category: normalizeString(breadcrumbs().pop() ?? ''),
            skuCode: normalizeString(sku),
            productId: normalizeString(productSlug),
            productType: normalizeString(productType),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_SELECT_PRODUCT ||
        type === DigitalDataMutation.TEST_RIDE_SELECT_PDP) {
        const { sku, url } = payload;
        const maybeCategory = (url ?? '').split('/')[5];
        dd.eventInfo = {
            category: normalizeString(maybeCategory ? maybeCategory : breadcrumbs().pop() ?? ''),
            skuCode: normalizeString(sku),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.ADD_PROMOCODE ||
        type === DigitalDataMutation.REMOVE_PROMOCODE) {
        const { code, cart } = payload;
        const serializedCart = serializeCart(cart, () => '');
        dd.data.cart = dd.data.cart || serializedCart;
        const cartRef = dd.data.cart;
        cartRef.total = serializedCart.total;
        cartRef.subTotal = serializedCart.subTotal;
        cartRef.shippingCost = serializedCart.shippingCost;
        cartRef.tax = serializedCart.tax;
        cartRef.voucher = serializedCart.voucher;
        cartRef.voucherDiscount = serializedCart.voucherDiscount;
        dd.eventInfo = {
            voucher: normalizeString(code),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_360) {
        if (!isPDPPage()) {
            return;
        }
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_CLICK_THUMBNAIL) {
        if (!isPDPPage()) {
            return;
        }
        const { id } = payload;
        dd.eventInfo = {
            producId: dd.data.product.id,
            asset: id.split('/').pop() ?? '',
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_CLICK_SPECIFICATION) {
        const { specification } = payload;
        dd.eventInfo = {
            specification: normalizeString(specification),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.FAQ_LISTING) {
        const { sections } = payload;
        const items = [];
        sections.forEach((section) => {
            section.items.forEach((item) => {
                const node = item.content?.[':itemsOrder']?.[0];
                if (node) {
                    items.push({
                        id: item.content?.[':items']?.[node]?.id ?? '',
                        topic: item.itemTitle,
                    });
                }
            });
        });
        const listId = 'faq-list';
        const listItems = items.map((item) => {
            return {
                faq: item,
            };
        });
        // remove old listing with same id
        dd.data.listings = dd.data.listings.filter((l) => l.listing.id !== listId);
        if (listItems.length) {
            dd.data.listings.push({
                listing: {
                    name: listId,
                    id: listId,
                    listItems,
                    category: 'faq',
                    query: '',
                    resultCount: listItems.length,
                    pagesCount: 0,
                    currentPage: 0,
                    sortBy: '',
                    filterBy: {},
                },
            });
        }
        return;
    }
    else if (type === DigitalDataMutation.NOTIFY_ME_CONFIRMATION) {
        const metadata = payload;
        dd.eventInfo = {
            skuCode: normalizeString(metadata.sku),
            marketingChannel: normalizeString(metadata.marketingChannel),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.EMAIL_SUBSCRIPTION) {
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.RETURN_PAGE_VIEW ||
        type === DigitalDataMutation.RETURN_PAGE_ITEMS_VIEW ||
        type === DigitalDataMutation.RETURN_PAGE_PACKAGE_VIEW) {
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.NOTIFY_ME_ENTRY) {
        const metadata = payload;
        dd.eventInfo = {
            skuCode: normalizeString(metadata.sku),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_CHANGE_COLOR) {
        const metadata = payload;
        const color = normalizeString(metadata.color);
        dd.eventInfo = {
            skuCode: normalizeString(metadata.sku),
            color,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_CHANGE_SIZE) {
        const metadata = payload;
        const size = normalizeString(metadata.size);
        dd.eventInfo = {
            skuCode: normalizeString(metadata.sku),
            size,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_SKU_CHANGE) {
        const metadata = payload;
        const { sku } = metadata;
        const properties = [
            'transmission',
            'powerVersion',
            'loweredVersion',
            'productOptions',
            'rotation',
            'startingMethod',
            'shaftLength',
            'controlMethod',
        ];
        properties.forEach((prop) => {
            if (metadata[prop]) {
                dd.eventInfo = {
                    skuCode: normalizeString(sku),
                    [prop]: normalizeString(metadata[prop]),
                };
            }
        });
        dd.event = type;
    }
    else if (type === DigitalDataMutation.FAQ_EXPANDED) {
        const metadata = payload;
        // Only track FAQ events on PDP
        if (!isPDPPage()) {
            return;
        }
        dd.eventInfo = {
            faq: {
                id: normalizeString(metadata.id),
                topic: metadata.topic,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.NAVIGATION) {
        const metadata = payload;
        dd.eventInfo = {
            pageSection: metadata.pageSection ? normalizeString(metadata.pageSection) : '',
            navigationGroup: metadata.navigationGroup ? normalizeString(metadata.navigationGroup) : '',
            navigationItem: normalizeString(metadata.navigationItem),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CHECKOUT_DEALER_SELECT) {
        const metadata = payload;
        if (metadata) {
            dd.eventInfo = {
                location: normalizeLocation(metadata.location),
                dealerId: normalizeString(metadata.dealerId),
                dealerName: normalizeString(metadata.dealerName),
            };
            dd.event = type;
            try {
                const dealerId = normalizeString(metadata.dealerId);
                localStorage.setItem(ANALYTICS_CART_DEALER_ID, dealerId);
            }
            catch {
                // do nothing
            }
        }
        else {
            return;
        }
    }
    else if (type === DigitalDataMutation.CHECKOUT_DEALER_SEARCH) {
        const metadata = payload;
        if (metadata) {
            dd.eventInfo = {
                location: normalizeLocation(metadata.location),
            };
            dd.event = type;
        }
        else {
            return;
        }
    }
    else if (type === DigitalDataMutation.CHECKOUT_CONFIRMATION) {
        const metadata = payload;
        const transaction = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        transaction.transactionId = metadata.orderId;
        transaction.cartId = transaction.id;
        transaction.paymentMethod = normalizeString(metadata.paymentMethod, '');
        delete transaction.id;
        dd.data.cart = {};
        dd.data.transaction = transaction;
        try {
            dd.data.transaction.shippingDealer = localStorage.getItem(ANALYTICS_CART_DEALER_ID) ?? '';
        }
        catch {
            // do nothing
        }
        dd.eventInfo = {
            marketingChannel: normalizeString(metadata.marketingChannel),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CHECKOUT_PAYMENT_DETAILS) {
        const metadata = payload;
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CHECKOUT_REVIEW) {
        const metadata = payload;
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CHECKOUT_SHIPPING_DETAILS) {
        const metadata = payload;
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CHECKOUT_LOGIN) {
        const metadata = payload;
        if (dd.data.user.profile.profileInfo.userName === GUEST_USER_NAME) {
            dd.data.user.profile.profileInfo.userName = metadata.userName || GUEST_USER_NAME;
        }
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CART_VIEW ||
        type === DigitalDataMutation.SHOPPING_CART_POPUP_PROCEED_CLICK) {
        const metadata = payload;
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        dd.eventInfo = {};
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CLEAR_LINE_ITEMS) {
        const metadata = payload;
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory, true);
        dd.event = 'clear-line-items';
        dd.eventInfo = {};
    }
    else if (type === DigitalDataMutation.REMOVE_FROM_CART) {
        const metadata = payload;
        const sku = normalizeString(metadata.sku);
        const entry = metadata.entry;
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        const lineItems = dd.data.cart.lineItems;
        const target = lineItems?.find((item) => item.product.skuCode === sku) ??
            (entry ? entryToLineItem(entry, metadata.getPdpLinkByCategory, '', rate) : null);
        dd.eventInfo = {
            id: normalizeString(target?.product.id ?? ''),
            quantity: metadata.quantity,
            skuCode: sku,
            masterSkuCode: normalizeString(target?.product.masterSkuCode || ''),
            name: target?.product.name ?? '',
            category: target?.product.category,
            parentCategory: target?.product.parentCategory,
            rootCategory: target?.product.rootCategory,
            manufacturer: target?.product.manufacturer,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.ADD_TO_CART) {
        const metadata = payload;
        dd.data.cart = serializeCart(metadata.cart, metadata.getPdpLinkByCategory);
        const sku = normalizeString(metadata.sku);
        const lineItems = dd.data.cart.lineItems;
        const target = lineItems ? lineItems.find((item) => item.product.skuCode === sku) : null;
        dd.eventInfo = {
            id: normalizeString(target?.product.id ?? ''),
            quantity: metadata.quantity,
            skuCode: sku,
            masterSkuCode: normalizeString(target?.product.masterSkuCode || ''),
            name: target?.product.name ?? '',
            category: target?.product.category,
            parentCategory: target?.product.parentCategory,
            rootCategory: target?.product.rootCategory,
            manufacturer: target?.product.manufacturer,
        };
        dd.event = type;
    }
    else if ([
        DigitalDataMutation.FASLET_LOADED,
        DigitalDataMutation.FASLET_RECOMMENDED_SIZE,
        DigitalDataMutation.FASLET_ADDED_TO_CART,
    ].includes(type)) {
        const metadata = payload;
        const sku = normalizeString(metadata.sku);
        const masterSkuCode = normalizeString(metadata.masterSkuCode);
        const category = normalizeString(metadata.category);
        const id = normalizeString(metadata.productId ?? '');
        dd.eventInfo = {
            quantity: '1',
            skuCode: sku,
            id,
            masterSkuCode,
            name: metadata.name,
            category,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CP_VIEW) {
        const metadata = payload;
        dd.eventInfo = {};
        dd.data.page = metadata.page;
        dd.data.website = metadata.website;
        dd.event = type;
    }
    else if (type === DigitalDataMutation.EDP_VIEW ||
        type === DigitalDataMutation.EDP_SEND_INQUIRY_VIEW) {
        const metadata = payload;
        dd.eventInfo = {};
        dd.data.destination = metadata.data.destination;
        const destinationListItems = metadata.data?.destinationListing;
        const unitListItems = metadata.data?.unitListing;
        const destinationListingId = ListingMap.destinationListing;
        const unitListingId = ListingMap.unitListing;
        dd.data.listings = [
            {
                listing: {
                    name: destinationListingId,
                    id: destinationListingId,
                    listItems: destinationListItems,
                    category: 'destinations',
                    query: '',
                    resultCount: destinationListItems?.length,
                    pagesCount: Math.ceil(destinationListItems.length / 3),
                    sortBy: '',
                    filterBy: {},
                },
            },
            {
                listing: {
                    name: unitListingId,
                    id: unitListingId,
                    listItems: unitListItems,
                    query: '',
                    resultCount: unitListItems?.length,
                    pagesCount: Math.ceil(unitListItems.length / 3),
                    sortBy: '',
                    filterBy: {},
                },
            },
        ];
        dd.event = type;
    }
    else if (type === DigitalDataMutation.ELP_VIEW || type === DigitalDataMutation.ELP_FILTER) {
        const metadata = payload;
        const listItems = metadata.listItems;
        const listId = ListingMap[type];
        dd.data.listings = [
            {
                listing: {
                    name: listId,
                    id: listId,
                    listItems,
                    category: metadata.category,
                    query: '',
                    resultCount: metadata.resultCount,
                    pagesCount: metadata.pagesCount,
                    sortBy: '',
                    filterBy: {},
                },
            },
        ];
        dd.eventInfo = {
            filterBy: metadata.filterBy,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PDP_VIEW) {
        const { product, currentSku } = payload;
        const serializedProduct = serializeProductData(product, currentSku);
        if (isEqual(dd.data.product, serializedProduct)) {
            // due to React component lifecycle we can get multiple PDP_VIEW events
            // with the same product data, so we need to ignore them
            return;
        }
        dd.data.product = {
            masterSkuCode: normalizeString(getMasterVariantSku(product.variants)),
            ...serializedProduct,
        };
        dd.eventInfo = {
            type: product.type,
            productHasConfigurator: product.variants
                .find((v) => v.master)
                ?.attributes.find((attr) => attr.name === PRODUCT_HAS_CONFIGURATOR)?.value ?? false,
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.RELATED_ITEMS_WIDGET_CLICK) {
        const { product } = payload;
        const { id, category, parentCategory, skuCode, name, rootCategory } = serializeProductData(product);
        dd.eventInfo = {
            product: {
                skuCode,
                productId: id,
                name,
                type: product.type,
                category,
                parentCategory,
                rootCategory,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CLICK_FROM_RELATED_WIDGET) {
        const { product } = payload;
        const { id, category, parentCategory, skuCode, name, rootCategory } = serializeProductData(product);
        dd.eventInfo = {
            product: {
                skuCode,
                productId: id,
                name,
                type: product.type,
                category,
                parentCategory,
                rootCategory,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.SET_USER) {
        dd.data.user = payload;
        return;
    }
    else if (type === DigitalDataMutation.SET_PAGE) {
        dd.data.page = payload;
        return;
    }
    else if (type === DigitalDataMutation.SET_WEB_SITE) {
        dd.data.website = payload;
        return;
    }
    else if (type === DigitalDataMutation.RMV_CMP || type === DigitalDataMutation.ADD_CMP) {
        const metadata = payload;
        const listItems = metadata.products.map((p) => ({
            product: mapToAdobeDataLayerProduct(p, metadata.pdpPageUrl),
        }));
        const listId = ListingMap[type];
        dd.eventInfo = {
            id: normalizeString(metadata.id),
        };
        // remove old listing with same id
        dd.data.listings = dd.data.listings.filter((l) => l.listing.id !== listId);
        if (listItems.length) {
            dd.data.listings.push({
                listing: {
                    name: listId,
                    id: listId,
                    listItems,
                },
            });
        }
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PLP_VIEW) {
        const metadata = payload;
        const listItems = metadata.products.map((p) => ({
            product: mapToAdobeDataLayerProduct(p, metadata.pdpPageUrl),
        }));
        const listId = metadata.cqType in ListingMap ? ListingMap[metadata.cqType] : ListingMap.unknown;
        dd.data.listings = [
            {
                listing: {
                    name: listId,
                    id: listId,
                    listItems,
                    categoryId: metadata.category.id,
                    category: metadata.category.name,
                    query: '',
                    sortBy: metadata.sortBy,
                    // @todo: add filterBy
                    filterBy: {},
                    ...cursorToPagination(metadata.cursor),
                },
            },
        ];
        dd.event = type;
    }
    else if (type === DigitalDataMutation.PLP_BLOOMREACH_VIEW) {
        const metadata = payload;
        const listItems = metadata.products.map((p) => ({
            product: mapToAdobeDataLayerBloomreachProduct(p, rate),
        }));
        const listId = metadata.cqType in ListingMap ? ListingMap[metadata.cqType] : ListingMap.unknown;
        dd.data.listings = [
            {
                listing: {
                    name: listId,
                    id: listId,
                    listItems,
                    categoryId: metadata?.category?.id ?? '',
                    category: metadata?.category?.name ?? '',
                    query: '',
                    sortBy: BLOOMREACH_QUERY_ANALYTIC_SORT_MAPPING[metadata.sortBy].replace(' ', '.'),
                    filterBy: {},
                    ...cursorToPagination(metadata.cursor),
                },
            },
        ];
        dd.event = DigitalDataMutation.PLP_VIEW;
    }
    else if (type === DigitalDataMutation.TEST_RIDE_VIEW_SEGMENT ||
        type === DigitalDataMutation.TEST_RIDE_VIEW_CATEGORY) {
        const { products, links } = payload;
        if (!links.length) {
            return;
        }
        const listItems = products.map((p) => {
            const inRiverIdValue = getInRiverId(attrValue(p, 'inRiverId') ?? '');
            const link = links.find((l) => l.key === inRiverIdValue);
            const publishedCategory = p.categories.find((c) => c.published);
            const parentCategory = publishedCategory?.parent;
            const rootCategory = parentCategory?.parent;
            const categoryName = categoryNameFromKey(publishedCategory?.key ?? '');
            const parentCategoryName = categoryNameFromKey(parentCategory?.key ?? '');
            const rootCategoryName = categoryNameFromKey(rootCategory?.key ?? '');
            return {
                product: {
                    skuCode: normalizeString(p.sku),
                    name: normalizeString(attrValue(p, 'productNameEn'), p.name),
                    manufacturer: 'yamaha',
                    color: normalizeString(attrValue(p, 'colourName'), ''),
                    size: normalizeString(attrValue(p, 'size'), ''),
                    url: link
                        ? getTestRideUrl({
                            url: link.value,
                            sku: p.sku,
                        })
                        : '',
                    category: categoryName,
                    parentCategory: parentCategoryName,
                    rootCategory: rootCategoryName,
                    categoryId: normalizeString(publishedCategory?.id ?? ''),
                },
            };
        });
        const listId = ListingMap[type];
        // remove old listing with same id
        dd.data.listings = dd.data.listings.filter((l) => l.listing.id !== listId);
        if (listItems.length) {
            const location = window?.location?.href ?? '';
            const searchParams = new URL(location).searchParams;
            dd.data.listings.push({
                listing: {
                    name: listId,
                    id: listId,
                    listItems,
                    currentPage: Number(searchParams.get('page') ?? 1),
                    filterBy: {
                        driversLicense: normalizeString(searchParams.get('driverLicense') ?? ''),
                    },
                },
            });
        }
        dd.event = breadcrumbs().length === 2 ? DigitalDataMutation.TEST_RIDE_VIEW_CATEGORY : type;
    }
    else if (type === DigitalDataMutation.SEGMENT_PANEL_LOAD) {
        const { segments } = payload;
        const listingId = 'category-list-all';
        dd.data.listings = dd.data.listings.filter((l) => l.listing.id !== listingId);
        dd.data.listings.push({
            listing: {
                name: listingId,
                id: listingId,
                listItems: segments.map((s) => {
                    return {
                        product: {
                            category: normalizeString(s),
                            parentCategory: normalizeString(breadcrumbs().shift() ?? ''),
                            rootCategory: normalizeString('unit'),
                        },
                    };
                }),
            },
        });
        dd.event = type;
    }
    else if (type === DigitalDataMutation.EVENTS_DETAILS_VIEW ||
        type === DigitalDataMutation.NEWS_DETAILS_VIEW) {
        const { headline, publishDate, label } = payload;
        const url = `${window.location.href}`;
        const id = url
            .split('/')
            .filter((item) => item)
            .slice(-2)
            .join('/');
        dd.data.newsEvent = {
            id,
            headline,
            publishDate,
            label,
            url,
        };
        dd.eventInfo = {
            newsEvent: {
                id,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.NEWS_ITEM_CLICK ||
        type === DigitalDataMutation.EVENTS_ITEM_CLICK) {
        const { articleResponse, id } = payload;
        const listingId = ListingMap[type];
        const searchParams = new URLSearchParams(window.location.search);
        dd.data.listings = dd.data.listings.filter((l) => l.listing.id !== listingId);
        dd.data.listings.push({
            listing: {
                name: listingId,
                id: listingId,
                listItems: articleResponse.results.map(({ title, date, link, dateFrom }) => {
                    const publishDate = date ?? dateFrom;
                    return {
                        newsEvent: {
                            id: link
                                .split('/')
                                .filter((item) => item)
                                .slice(-2)
                                .join('/'),
                            headline: title,
                            publishDate: publishDate ? format(publishDate, DATE_FORMAT['dd/MM/yyyy']) : '',
                            url: `${window.location.origin}${link}`,
                        },
                    };
                }),
                category: listingId,
                query: '',
                resultCount: articleResponse.total,
                pagesCount: Math.ceil(articleResponse.total / articleResponse.limit),
                currentPage: Number(searchParams.get(PARAMS_KEYS.page) ?? 1),
                sortBy: '',
                filterBy: {},
            },
        });
        dd.eventInfo = {
            newsEvent: {
                id,
            },
        };
        dd.event = type;
    }
    else if ([
        DigitalDataMutation.NEWS_LIST_VIEW,
        DigitalDataMutation.EVENT_LIST_VIEW,
        DigitalDataMutation.NEWS_LIST_FILTER,
        DigitalDataMutation.EVENT_LIST_FILTER,
    ].includes(type)) {
        const { articleResponse, params: { currentPage, years, categories }, } = payload;
        const listingId = ListingMap[type];
        const mappedCategories = categories.map((category) => normalizeString(category.split('/').at(-1)));
        dd.data.listings = dd.data.listings.filter((l) => l.listing.id !== listingId);
        dd.data.listings.push({
            listing: {
                name: listingId,
                id: listingId,
                listItems: articleResponse.results.map(({ title, date, link, dateFrom }) => {
                    const publishDate = date ?? dateFrom;
                    return {
                        newsEvent: {
                            id: link
                                .split('/')
                                .filter((item) => item)
                                .slice(-2)
                                .join('/'),
                            headline: title,
                            publishDate: publishDate ? format(publishDate, DATE_FORMAT['dd/MM/yyyy']) : '',
                            url: `${window.location.origin}${link}`,
                        },
                    };
                }),
                category: listingId,
                query: '',
                resultCount: articleResponse.total,
                pagesCount: Math.ceil(articleResponse.total / articleResponse.limit),
                currentPage,
                sortBy: '',
                filterBy: {
                    years,
                    categories: mappedCategories,
                },
            },
        });
        dd.eventInfo = {
            filterBy: {
                years,
                categories: mappedCategories,
            },
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.CHECKOUT_EXCLUSION_CONFIRMATION) {
        const { dealerId, dealerName } = payload;
        dd.eventInfo = {
            dealerId: normalizeString(dealerId),
            dealerName: normalizeString(dealerName),
        };
        dd.event = type;
    }
    else if (type === DigitalDataMutation.RETURN_PAGE_REQUEST_VIEW ||
        type === DigitalDataMutation.RETURN_PAGE_REQUEST_SUBMISSION_VIEW) {
        const data = payload;
        dd.eventInfo = data;
        dd.event = type;
    }
    else if (type === DigitalDataMutation.ABTEST_BANNER_VIEW) {
        const { tests } = payload;
        dd.data.tests = Object.assign({}, dd.data.tests, tests);
        dd.eventInfo = tests;
        dd.event = type;
    }
    else if (type === DigitalDataMutation.ABTEST_BANNER_CLICK) {
        const { tests } = payload;
        dd.data.tests = Object.assign({}, dd.data.tests, tests);
        dd.eventInfo = tests;
        dd.event = type;
    }
    else if (type === DigitalDataMutation.OPEN_PARTS_CATALOG) {
        const { url } = payload;
        dd.eventInfo = {
            url,
        };
        dd.event = type;
    }
    if (dd?.data?.page) {
        dd.data.page = {
            ...dd.data.page,
            queryString: window.location.search,
            title: document.title,
        };
    }
    adobeLaunch.push(dd);
}
